import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'draft';

export type BadgeStyle = 'soft' | 'solid' | 'outline';

const SOFT_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-phx-surface-2 text-phx-text-2',
  primary: 'bg-phx-primary-tint text-phx-primary-active',
  success: 'bg-phx-success-tint text-phx-success-text',
  warning: 'bg-phx-warning-tint text-phx-warning-text',
  danger: 'bg-phx-danger-tint text-phx-danger-text',
  info: 'bg-phx-info-tint text-phx-info-text',
  draft: 'bg-phx-draft-tint text-phx-draft-text',
};

const SOLID_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-phx-text text-phx-text-inv',
  primary: 'bg-phx-primary text-phx-primary-fg',
  success: 'bg-phx-success text-phx-success-fg',
  warning: 'bg-phx-warning text-white',
  danger: 'bg-phx-danger text-phx-danger-fg',
  info: 'bg-phx-info text-phx-info-fg',
  draft: 'bg-phx-draft text-phx-text-inv',
};

const OUTLINE_CLASSES: Record<BadgeTone, string> = {
  neutral: 'border border-phx-border-3 text-phx-text-2',
  primary: 'border border-phx-primary text-phx-primary',
  success: 'border border-phx-success text-phx-success',
  warning: 'border border-phx-warning text-phx-warning-text',
  danger: 'border border-phx-danger text-phx-danger',
  info: 'border border-phx-info text-phx-info',
  draft: 'border border-phx-draft text-phx-draft-text',
};

const BASE_BADGE =
  'phx-badge inline-flex items-center gap-[3px] h-[18px] px-phx-3 ' +
  'text-phx-xs font-medium leading-none whitespace-nowrap rounded-phx-sm tracking-[0.01em]';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'style'> {
  tone?: BadgeTone;
  appearance?: BadgeStyle;
  leadingIcon?: PhxIconName;
  trailingIcon?: PhxIconName;
  children?: ReactNode;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  {
    tone = 'neutral',
    appearance: variant = 'soft',
    leadingIcon,
    trailingIcon,
    children,
    className,
    ...rest
  },
  ref
) {
  const styleClasses =
    variant === 'soft'
      ? SOFT_CLASSES[tone]
      : variant === 'solid'
        ? SOLID_CLASSES[tone]
        : OUTLINE_CLASSES[tone];
  return (
    <span
      ref={ref}
      data-tone={tone}
      data-bstyle={variant}
      className={cn(BASE_BADGE, styleClasses, className)}
      {...rest}
    >
      {leadingIcon ? <Icon name={leadingIcon} size={10} strokeWidth={2.25} /> : null}
      {children}
      {trailingIcon ? <Icon name={trailingIcon} size={10} strokeWidth={2.25} /> : null}
    </span>
  );
});
